// T-473 — cockpit decisionale ristorante. Presentazionale e locale: non salva
// nulla e non presume costi di personale, struttura, POS o utile netto.
import { useMemo, useState } from 'react';
import { computeRestaurantDecision } from '@trackeat/shared';

export type RestaurantDecisionRowStatus = 'ready' | 'incomplete' | 'anomaly';

export interface RestaurantDecisionCockpitRow {
  id: string;
  name: string;
  status: RestaurantDecisionRowStatus;
  costPerPortion: number | null;
  sellingPriceCentsGross: number | null;
  vatRatePct: number;
  /** Messaggio della diagnostica ricetta o dell'anomalia prezzo. */
  detail?: string;
  /** Alimento da correggere quando lo stato deriva da un costo anomalo. */
  blockerFoodId?: string;
}

export interface RestaurantDecisionCockpitProps {
  rows: readonly RestaurantDecisionCockpitRow[];
  target: number;
  onTargetChange: (target: number) => void;
  onFixRecipe?: (recipeId: string) => void;
  onOpenDocument?: (foodId: string) => void;
}

export interface RestaurantDecisionSummary {
  total: number;
  ready: number;
  anomaly: number;
  incomplete: number;
}

export function summarizeRestaurantDecisionRows(rows: readonly RestaurantDecisionCockpitRow[]): RestaurantDecisionSummary {
  return rows.reduce<RestaurantDecisionSummary>((summary, row) => {
    summary.total++;
    summary[row.status]++;
    return summary;
  }, { total: 0, ready: 0, anomaly: 0, incomplete: 0 });
}

const eur = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const targets = [28, 30, 32, 35] as const;
const statusLabel: Record<RestaurantDecisionRowStatus, string> = {
  ready: 'Pronta', incomplete: 'Incompleta', anomaly: 'Costo da verificare',
};
const statusColor: Record<RestaurantDecisionRowStatus, string> = {
  ready: '#15803d', incomplete: '#b45309', anomaly: '#b91c1c',
};
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 13 } as const;

function money(value: number | null): string { return value == null ? '—' : eur.format(value); }
function percent(value: number | null): string { return value == null ? '—' : `${value.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`; }

/**
 * Superficie autonoma da montare nel Report ristorante. I dati arrivano dal
 * chiamante: status non-ready o input incompleti non diventano mai €0.
 */
export function RestaurantDecisionCockpit({ rows, target, onTargetChange, onFixRecipe, onOpenDocument }: RestaurantDecisionCockpitProps): JSX.Element {
  const [stressRaw, setStressRaw] = useState('0');
  const stress = Number(stressRaw.replace(',', '.'));
  const usableStress = Number.isFinite(stress) && stress >= 0 ? stress : null;
  const summary = useMemo(() => summarizeRestaurantDecisionRows(rows), [rows]);
  const decisions = useMemo(() => {
    const statusRank: Record<RestaurantDecisionRowStatus, number> = { anomaly: 0, incomplete: 1, ready: 2 };
    return rows.map((row) => ({
      row,
      decision: row.status === 'ready' && usableStress != null ? computeRestaurantDecision({
        costPerPortion: row.costPerPortion,
        sellingPriceCentsGross: row.sellingPriceCentsGross,
        vatRatePct: row.vatRatePct,
        targetFoodCostPct: target,
        costStressPct: usableStress,
      }) : null,
    })).sort((a, b) => statusRank[a.row.status] - statusRank[b.row.status] || a.row.name.localeCompare(b.row.name));
  }, [rows, target, usableStress]);
  const actions = [...decisions]
    .filter((item): item is typeof item & { decision: NonNullable<typeof item.decision> } => item.decision?.status === 'ready')
    .filter((item) => (item.decision.listPriceDeltaGross ?? 0) > 0.005)
    .sort((a, b) => (b.decision.listPriceDeltaGross ?? 0) - (a.decision.listPriceDeltaGross ?? 0));

  return (
    <section className="fc-decisions" aria-label="Decisioni menu">
      <div style={{ ...card, marginBottom: 12, borderColor: '#bfdbfe', background: '#eff6ff' }}>
        <h2 style={{ margin: 0, fontSize: 20, color: '#0f172a' }}>Piatti e simulazione prezzi</h2>
        <p style={{ margin: '5px 0 0', color: '#475569', fontSize: 12.5, lineHeight: 1.4 }}>
          Prezzi e costi attuali, indipendenti dal periodo acquisti. Il margine sulle materie precede personale e costi di struttura.
        </p>
        <p style={{ margin: '5px 0 0', color: '#475569', fontSize: 12.5, lineHeight: 1.4 }}>
          Voci con prezzo di vendita o marcate per il food cost. Il prezzo simulato indica quanto servirebbe per il target scelto: va valutato insieme a porzione, servizio e mercato.
        </p>
      </div>

      {summary.total > 0 && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={{ fontWeight: 750, color: '#0f172a', fontSize: 14 }}>
            Piatti registrati: {summary.ready}/{summary.total} calcolabili
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 7, marginTop: 10 }}>
            <div style={{ borderRadius: 10, background: '#f0fdf4', padding: '8px 6px', textAlign: 'center', color: '#166534' }}><strong style={{ display: 'block', fontSize: 18 }}>{summary.ready}</strong><small>Pronte</small></div>
            <div style={{ borderRadius: 10, background: '#fef2f2', padding: '8px 6px', textAlign: 'center', color: '#991b1b' }}><strong style={{ display: 'block', fontSize: 18 }}>{summary.anomaly}</strong><small>Da verificare</small></div>
            <div style={{ borderRadius: 10, background: '#fffbeb', padding: '8px 6px', textAlign: 'center', color: '#92400e' }}><strong style={{ display: 'block', fontSize: 18 }}>{summary.incomplete}</strong><small>Incomplete</small></div>
          </div>
        </div>
      )}

      <details className="fc-card" style={{ marginBottom: 12 }}>
        <summary>Imposta simulazione · target attuale {target}%</summary>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>Obiettivo della simulazione</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 9 }}>
          {targets.map((value) => (
            <button key={value} type="button" onClick={() => onTargetChange(value)} aria-pressed={target === value}
              style={{ border: `1px solid ${target === value ? '#2563eb' : '#cbd5e1'}`, background: target === value ? '#2563eb' : '#fff', color: target === value ? '#fff' : '#334155', borderRadius: 18, padding: '6px 11px', fontWeight: 700 }}>
              {value}%
            </button>
          ))}
        </div>
        <label className="fc-search">Obiettivo personalizzato % (1–100)
          <input type="number" min="1" max="100" step="0.5" value={target} onChange={(event) => {
            const value = Number(event.target.value); if (value > 0 && value <= 100) onTargetChange(value);
          }} />
        </label>
        <label style={{ display: 'block', marginTop: 12, fontSize: 12.5, color: '#475569' }}>
          Scenario aumento costo ingredienti % <small>(solo questa schermata)</small>
          <input inputMode="decimal" value={stressRaw} onChange={(event) => setStressRaw(event.target.value)}
            aria-label="Scenario aumento costo ingredienti percentuale"
            style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 5, border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 9px', fontSize: 16 }} />
        </label>
        {usableStress == null && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>Inserisci una percentuale maggiore o uguale a zero.</div>}
      </details>

      {actions.length > 0 && (
        <details className="fc-card" style={{ marginBottom: 12, borderColor: '#fde68a', background: '#fffbeb' }}>
          <summary>{actions.length} {actions.length === 1 ? 'piatto sopra il target' : 'piatti sopra il target'} · dettagli della simulazione</summary>
          <p>Il prezzo calcolato non è una raccomandazione automatica di aumento.</p>
          {actions.slice(0, 3).map(({ row, decision }) => (
            <div key={row.id} style={{ borderTop: '1px solid #fde68a', marginTop: 8, paddingTop: 8, fontSize: 12.5, color: '#78350f' }}>
              <b>{row.name}</b> · prezzo target {money(decision.targetGrossPrice)}
              {' '}({decision.listPriceDeltaGross != null && decision.listPriceDeltaGross > 0 ? '+' : ''}{money(decision.listPriceDeltaGross)} sul listino)
              {stress > 0 && <> · con stress: {money(decision.stressedTargetGrossPrice)}</>}
            </div>
          ))}
        </details>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {decisions.map(({ row, decision }) => {
          const unavailable = row.status !== 'ready' || decision?.status !== 'ready';
          const effectiveStatus: RestaurantDecisionRowStatus = row.status === 'ready' && unavailable ? 'incomplete' : row.status;
          return (
            <article key={row.id} style={{ ...card, opacity: unavailable ? 0.82 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0, fontWeight: 750, color: '#0f172a' }}>{row.name}</div>
                <span style={{ color: statusColor[effectiveStatus], fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{statusLabel[effectiveStatus]}</span>
              </div>
              {unavailable ? (
                <>
                  <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 12.5 }}>{row.detail ?? 'Completa costi, unità, porzioni e prezzo prima di decidere.'}</p>
                  {row.status === 'anomaly' && row.blockerFoodId && onOpenDocument ? (
                    <button type="button" onClick={() => onOpenDocument(row.blockerFoodId!)}
                      style={{ marginTop: 9, border: '1px solid #fecaca', background: '#fff1f2', color: '#991b1b', borderRadius: 9, padding: '7px 10px', fontWeight: 700 }}>
                      Apri documento di origine
                    </button>
                  ) : onFixRecipe ? (
                    <button type="button" onClick={() => onFixRecipe(row.id)}
                      style={{ marginTop: 9, border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e', borderRadius: 9, padding: '7px 10px', fontWeight: 700 }}>
                      Sistema ricetta
                    </button>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="fc-dish-numbers">
                    <div><small>Costo per porzione</small><strong>{money(row.costPerPortion)}</strong></div>
                    <div><small>Prezzo al cliente</small><strong>{money(decision.current.sellingGross)}</strong></div>
                    <div><small style={{ color: '#64748b' }}>Food cost attuale</small><strong style={{ display: 'block' }}>{percent(decision.current.foodCostPct)}</strong></div>
                    <div><small style={{ color: '#64748b' }}>Margine materie</small><strong style={{ display: 'block' }}>{money(decision.current.marginNet)} · {percent(decision.materialMarginPct)}</strong></div>
                  </div>
                  <details className="fc-method">
                    <summary>Simulazione prezzo per il target {target}%</summary>
                    <div className="fc-dish-numbers">
                    <div><small style={{ color: '#64748b' }}>Prezzo target lordo</small><strong style={{ display: 'block' }}>{money(decision.targetGrossPrice)}</strong></div>
                    <div><small style={{ color: '#64748b' }}>Delta listino</small><strong style={{ display: 'block', color: (decision.listPriceDeltaGross ?? 0) > 0 ? '#b45309' : '#15803d' }}>{money(decision.listPriceDeltaGross)}</strong></div>
                    {stress > 0 && <><div><small style={{ color: '#64748b' }}>Food cost con aumento</small><strong style={{ display: 'block' }}>{percent(decision.stressedFoodCostPct)}</strong></div>
                    <div><small style={{ color: '#64748b' }}>Target con aumento</small><strong style={{ display: 'block' }}>{money(decision.stressedTargetGrossPrice)}</strong></div></>}
                  </div>
                  <p style={{ margin: '10px 0 0', color: '#64748b', fontSize: 11.5, lineHeight: 1.35 }}>
                    Costo attuale {money(row.costPerPortion)} → scenario {money(decision.stressedCostPerPortion)}. Prezzi al netto IVA per i margini; target riconvertito al lordo IVA.
                  </p>
                  </details>
                </>
              )}
            </article>
          );
        })}
      </div>
      {rows.length === 0 && <div style={{ ...card, color: '#64748b', fontSize: 13 }}>Nessuna ricetta disponibile per il cockpit.</div>}
    </section>
  );
}
